import type { Diagram, MermaidErrorInfo } from '../../store/types.ts';

type PropertiesPanelProps = {
  open: boolean;
  onClose: () => void;
  diagram: Diagram | undefined;
  renderError: MermaidErrorInfo | null;
  zoom: number;
  onSetTheme: (theme: 'light' | 'dark') => void;
  onAddTag: (tag: string) => void;
  onRemoveTag: (tag: string) => void;
};

export function PropertiesPanel({
  open,
  onClose,
  diagram,
  renderError,
  zoom,
  onSetTheme,
  onAddTag,
  onRemoveTag,
}: PropertiesPanelProps) {
  if (!open) return null;

  const codeLines = diagram?.mermaidCode.split('\n').length || 0;
  const codeChars = diagram?.mermaidCode.length || 0;
  const revisionCount = diagram?.revisions.length || 0;
  const chatCount = diagram?.chat.length || 0;
  const tags = diagram?.tags || [];

  return (
    <div className="ds-props-panel">
      <div className="ds-props-panel__head">
        <span className="ds-props-panel__title">Properties</span>
        <button type="button" className="ds-icon-btn" onClick={onClose} aria-label="Close panel">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {!diagram ? (
        <div className="ds-props-panel__empty">No diagram selected</div>
      ) : (
        <div className="ds-props-panel__body">
          {/* Info section */}
          <div className="ds-props-panel__section">
            <div className="ds-props-panel__section-title">Info</div>
            <div className="ds-props-panel__row">
              <span>Title</span>
              <span className="ds-props-panel__value">{diagram.title}</span>
            </div>
            <div className="ds-props-panel__row">
              <span>Type</span>
              <span className="ds-props-panel__value">{diagram.type}</span>
            </div>
            <div className="ds-props-panel__row">
              <span>Created</span>
              <span className="ds-props-panel__value">{new Date(diagram.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="ds-props-panel__row">
              <span>Updated</span>
              <span className="ds-props-panel__value">{new Date(diagram.updatedAt).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Stats section */}
          <div className="ds-props-panel__section">
            <div className="ds-props-panel__section-title">Stats</div>
            <div className="ds-props-panel__row">
              <span>Lines</span>
              <span className="ds-props-panel__value">{codeLines}</span>
            </div>
            <div className="ds-props-panel__row">
              <span>Characters</span>
              <span className="ds-props-panel__value">{codeChars.toLocaleString()}</span>
            </div>
            <div className="ds-props-panel__row">
              <span>Revisions</span>
              <span className="ds-props-panel__value">{revisionCount}</span>
            </div>
            <div className="ds-props-panel__row">
              <span>Messages</span>
              <span className="ds-props-panel__value">{chatCount}</span>
            </div>
            <div className="ds-props-panel__row">
              <span>Zoom</span>
              <span className="ds-props-panel__value">{Math.round(zoom * 100)}%</span>
            </div>
          </div>

          {/* Theme section */}
          <div className="ds-props-panel__section">
            <div className="ds-props-panel__section-title">Theme</div>
            <div className="ds-props-panel__theme-btns">
              <button
                type="button"
                className={`ds-props-panel__theme-btn ${diagram.theme === 'light' ? 'ds-props-panel__theme-btn--active' : ''}`}
                onClick={() => onSetTheme('light')}
              >
                Light
              </button>
              <button
                type="button"
                className={`ds-props-panel__theme-btn ${diagram.theme === 'dark' ? 'ds-props-panel__theme-btn--active' : ''}`}
                onClick={() => onSetTheme('dark')}
              >
                Dark
              </button>
            </div>
          </div>

          {/* Tags section */}
          <div className="ds-props-panel__section">
            <div className="ds-props-panel__section-title">Tags</div>
            <div className="ds-props-panel__tags">
              {tags.map((tag) => (
                <span key={tag} className="ds-props-panel__tag">
                  {tag}
                  <button
                    type="button"
                    className="ds-props-panel__tag-remove"
                    onClick={() => onRemoveTag(tag)}
                    aria-label={`Remove tag ${tag}`}
                  >
                    &times;
                  </button>
                </span>
              ))}
              <button
                type="button"
                className="ds-props-panel__tag-add"
                onClick={() => {
                  const tag = window.prompt('Add tag')?.trim().toLowerCase();
                  if (tag && !tags.includes(tag)) onAddTag(tag);
                }}
              >
                + Tag
              </button>
            </div>
          </div>

          {/* Error section */}
          {renderError && (
            <div className="ds-props-panel__section">
              <div className="ds-props-panel__section-title ds-props-panel__section-title--error">Error</div>
              <div className="ds-props-panel__error">
                <div className="ds-props-panel__error-location">
                  {renderError.line ? `Line ${renderError.line}` : 'Unknown line'}
                  {renderError.column ? `, Col ${renderError.column}` : ''}
                </div>
                <pre className="ds-props-panel__error-msg">{renderError.raw}</pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
